import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDate,
  today,
  ZonedDateTime,
} from "@internationalized/date";
import { RangeValue } from "@react-types/shared";
import { useQuery, useQueryClient } from "react-query";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import WarningIcon from "@mui/icons-material/Warning";
import { User } from "firebase/auth";
// Local
import { DbRead, DbWrite, QBOAccountResponse } from "../../database";
import { logger } from "../../logging";
import { ExistingStiltInvoice } from "../../models/invoice";
import QuickbooksImportPage from "./QuickbooksImportPage";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { useUserPermissionsStore } from "../../store/user-permissions";
import ConnectionButtons from "../../components/Quickbooks/ConnectionButtons";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils/createToastMessageID";
import ViewSimplifiedInvoiceDialog from "../../components/Payments/ViewSimplifiedInvoiceDialog";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { QBOInvoiceResponse } from "./QuickbooksSyncErrorsContainer";
import { QBOInvoice } from "../../components/Quickbooks/CompareInvoiceDialog";
import DateRangePicker from "../../components/DateRangePicker";
import LoadingSpinner from "../../components/LoadingSpinner";
import { StyledTooltip } from "../../components/StyledTooltip";
import ImportSingleAllInvoicesContent from "../../components/Quickbooks/ImportSingleAllInvoicesContent";
import ImportSingleByCustomerContent from "../../components/Quickbooks/ImportSingleByCustomerContent";
import { ExistingCustomerWithLocations } from "../../models/customer";
import BaseModal from "../../components/BaseModal";
import ImportAllContent from "../../components/Quickbooks/ImportAllContent";
import ImportQBOAccountsDialog from "../../components/ImportQBOAccountsDialog";
import {
  ExistingGLAccount,
  GLAccount_CreateAPI,
  GLAccountManager,
} from "../../models/gl-account";
import { useAuthStore } from "../../store/firebase-auth";
import {
  EnumQBOImportJobType,
  QBOImportJob,
} from "../../models/qbo-import-job";
import QBOImportProgressIndicator from "../../components/QBOImportProgressIndicator";
// QBCustomerResponse and CustomerManager might not be needed here if backend handles creation
// import { Customer_CreateAPI, CustomerManager, QBCustomerResponse } from "../../models/customer";

/** cherry-picked imports, or all records for specified data type */
export type ImportOption = "single" | "all";
/** ImportOption === "all" */
export type DataTypeImportAll =
  | "customers"
  | "invoices"
  | "payments"
  | "pbItems"
  | "all";
/** ImportOption === "single" */
export type DataTypeImportSingle = "byCustomer" | "allInvoices";

const IMPORT_STEPS = [
  "accounts",
  "pricebook",
  "customers",
  "invoices_payments",
] as const;
export type ImportStep = (typeof IMPORT_STEPS)[number];

export default function QuickbooksImportContainer(props: {
  siteKey: string;
}): JSX.Element {
  const { addToastMessage } = useToastMessageStore();
  const { siteKeyDoc, loading: siteKeyDocLoading } = useSiteKeyDocStore();
  const { siteKeyUserPermissions: userPermissions } = useUserPermissionsStore();
  const { siteKeyLocationList } = useSiteKeyLocationsStore();
  const { userDisplayNames: userDisplayNamesMap } = useUserDisplayNamesStore();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;

  const queryClient = useQueryClient();

  const [selectedImportOption, setSelectedImportOption] =
    useState<ImportOption>("single");
  const [selectedDataTypeAll, setSelectedDataTypeAll] =
    useState<DataTypeImportAll>("all");
  const [selectedDataTypeSingle, setSelectedDataTypeSingle] =
    useState<DataTypeImportSingle>("byCustomer");

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentImportStep: ImportStep = IMPORT_STEPS[currentStepIndex];
  const [isQBConnected, setIsQBConnected] = useState(false);

  const [gLAccountList, setGLAccountList] = useState<ExistingGLAccount[]>([]);
  const [isGetGLAccounts, setIsGetGLAccounts] = useState<boolean>(false);
  const [importQBODialogOpen, setImportQBODialogOpen] =
    useState<boolean>(false);
  const [qboChartOfAccounts, setQboChartOfAccounts] = useState<
    QBOAccountResponse[] | null
  >(null);
  const [isLoadingQBOAccounts, setIsLoadingQBOAccounts] =
    useState<boolean>(false);

  const [selectedCustomer, setSelectedCustomer] =
    useState<ExistingCustomerWithLocations | null>(null);

  const [invoices, setInvoices] = useState<ExistingStiltInvoice[]>([]);
  // fetch and display invoices whose issueDate lands within dateRange
  const [dateRange, setDateRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({ days: 30 }),
    end: today(getLocalTimeZone()),
  });

  // State for Customer Import step
  const [isImportingCustomers, setIsImportingCustomers] = useState(false);

  const [isImportingItems, setIsImportingItems] = useState(false);
  const [isImportingInvoicesAndPayments, setIsImportingInvoicesAndPayments] =
    useState(false);

  const [customersImportProgress, setCustomersImportProgress] =
    useState<QBOImportJob | null>(null);
  const subscribeCustomerImportMounted = useRef(false);

  const [itemsImportProgress, setItemsImportProgress] =
    useState<QBOImportJob | null>(null);
  const subscribeItemsImportMounted = useRef(false);

  const [
    invoicesAndPaymentsImportProgress,
    setInvoicesAndPaymentsImportProgress,
  ] = useState<QBOImportJob | null>(null);
  const subscribeInvoicesAndPaymentsImportMounted = useRef(false);

  // State for QBO Record Counts
  const [qboRecordCounts, setQboRecordCounts] = useState<Record<
    string,
    number
  > | null>(null);
  const [isLoadingRecordCounts, setIsLoadingRecordCounts] =
    useState<boolean>(false);

  // State for Stilt Record Counts
  const [stiltRecordCounts, setStiltRecordCounts] = useState<Record<
    string,
    number
  > | null>(null);
  const [isLoadingStiltRecordCounts, setIsLoadingStiltRecordCounts] =
    useState<boolean>(false);

  const [moreInfoBtnStates, setMoreInfoBtnStates] = useState<{
    importOptions: {
      single: boolean;
      all: boolean;
    };
    singleOption: {
      byCustomer: boolean;
      byInvoice: boolean;
    };
  }>({
    importOptions: { single: false, all: false },
    singleOption: { byCustomer: false, byInvoice: false },
  });

  const [viewInvoiceDialogState, setViewInvoiceDialogState] = useState<{
    isOpen: boolean;
    invoice: ExistingStiltInvoice | null;
    qboInvoice: QBOInvoiceResponse | null;
    areQboInvoicesLoading: boolean;
  }>({
    isOpen: false,
    invoice: null,
    qboInvoice: null,
    areQboInvoicesLoading: false,
  });

  /*
  const isQuickbooksDisabled = useMemo(() => {
    if (siteKeyDocLoading || !siteKeyDoc) return false;
    return (
      !siteKeyDoc.customizations.accounting?.pushToCodat &&
      !siteKeyDoc.customizations.accounting?.pushToConductor &&
      !siteKeyDoc.customizations.accounting?.pushToQBO
    );
  }, [siteKeyDocLoading, siteKeyDoc]);
  */

  // TODO: tell them if selected customer has not been pushed

  // INVOICES -- SINGLE IMPORT, ALL INVOICES
  // fetch invoices whose issueDate lands within dateRange
  useEffect(() => {
    function getInvoices() {
      if (selectedDataTypeSingle !== "allInvoices") return undefined;
      return DbRead.invoices.subscribeAllInvoicesInDateRange({
        siteKey: props.siteKey,
        dateRange: dateRange,
        onChange: setInvoices,
        onError: logger.error,
      });
    }
    const unsubscribe = getInvoices();
    return () => unsubscribe && unsubscribe();
  }, [props.siteKey, dateRange, selectedDataTypeSingle]);

  // INVOICES -- SINGLE IMPORT, BY CUSTOMER
  // fetch all invoices for a specific customer
  useEffect(() => {
    function getInvoicesByCustomer() {
      if (!selectedCustomer) return undefined;
      if (selectedDataTypeSingle !== "byCustomer") return undefined;
      return DbRead.invoices.subscribeByCustomerID({
        siteKey: props.siteKey,
        customerID: selectedCustomer.id,
        onChange: setInvoices,
        onError: logger.error,
      });
    }
    const unsubscribe = getInvoicesByCustomer();
    return () => unsubscribe && unsubscribe();
  }, [props.siteKey, selectedCustomer, selectedDataTypeSingle]);

  const acctRefIDs: string[] = useMemo(() => {
    const ids: string[] = [];
    for (const inv of invoices) {
      if (inv.customData.accountingRefID)
        ids.push(inv.customData.accountingRefID);
    }
    return ids;
  }, [invoices]);

  // stable query key -- we'll handle the changing IDs manually
  const queryKey = `qbImportContainer_qboInvoices_${props.siteKey}`;

  const prevDataRef = useRef<{
    acctRefIDs: Set<string>;
    invoiceIDs: Set<string>;
  }>({ acctRefIDs: new Set(), invoiceIDs: new Set() });

  useEffect(() => {
    prevDataRef.current.acctRefIDs = new Set(acctRefIDs);
  }, [acctRefIDs]);

  // MANUALLY TRIGGER REFETCH WHEN STILT INVOICES CHANGE
  useEffect(() => {
    const currentInvoiceIDs: Set<string> = new Set();
    for (const inv of invoices) currentInvoiceIDs.add(inv.id);

    if (prevDataRef.current.invoiceIDs === currentInvoiceIDs) return;
    prevDataRef.current.invoiceIDs = currentInvoiceIDs;

    queryClient.invalidateQueries(queryKey);
  }, [invoices, queryClient, queryKey]);

  useEffect(() => {
    if (siteKeyDoc && siteKeyDoc.customizations.accounting?.qboRealmID) {
      setIsQBConnected(true);
    } else {
      setIsQBConnected(false);
    }
  }, [siteKeyDoc]);

  useEffect(() => {
    if (!isQBConnected) return undefined;

    async function getGLAccountList() {
      const unsubscribe = DbRead.gLAccounts.subscribeAll({
        siteKey: props.siteKey,
        onChange: (fetchedGLAccountList: ExistingGLAccount[]) => {
          setGLAccountList(fetchedGLAccountList);
        },
        onError: (error) =>
          logger.error(`Error in getGLAccountList: ${error.message}`),
      });
      return unsubscribe;
    }
    const unsubscribePromise = getGLAccountList();
    return () => {
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [props.siteKey, isQBConnected]);

  const {
    data: qboInvoices,
    isLoading: qboInvoicesLoading,
    isFetching: qboInvoicesFetching,
  } = useQuery(
    queryKey,
    async function qboInvoicesQueryFn() {
      // if we don't store the accountingRefIDs in a ref, we won't get up-to-date IDs when this executes
      const currentIDs = Array.from(prevDataRef.current.acctRefIDs);
      if (currentIDs.length === 0) return [];
      // get existing data from cache
      const existingData =
        queryClient.getQueryData<QBOInvoiceResponse[]>(queryKey) || [];
      const existingIDs = new Set();
      for (const qboInv of existingData) existingIDs.add(qboInv.id);

      // track IDs that are not in the cache
      const idsToFetch: string[] = [];
      for (const id of currentIDs) {
        if (existingIDs.has(id)) continue;
        idsToFetch.push(id);
      }

      if (idsToFetch.length === 0) {
        // return cached data if it exists
        if (existingData.length > 0) return existingData;
        // otherwise, do a fresh fetch of all IDs (no data in cache)
        return DbRead.invoices.qboListByAcctRefID(props.siteKey, currentIDs);
      }
      // only fetch new IDs
      const newInvoices = await DbRead.invoices.qboListByAcctRefID(
        props.siteKey,
        idsToFetch,
      );
      // IMPORTANT - retain access to existing data
      return [...existingData, ...newInvoices];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000 * 120, // 2 hours
      cacheTime: 60000 * 120, // 2 hours

      enabled: acctRefIDs.length > 0,
    },
  );

  useEffect(() => {
    if (qboInvoicesLoading) return;
    if (!viewInvoiceDialogState.isOpen) return;
    if (!qboInvoices || !viewInvoiceDialogState.invoice) return;

    const acctRefID =
      viewInvoiceDialogState.invoice.customData.accountingRefID ?? null;

    setViewInvoiceDialogState((prev) => ({
      ...prev,
      qboInvoice: acctRefID
        ? (qboInvoices.find((i) => i.id === acctRefID) ?? null)
        : null,
      areQboInvoicesLoading: false,
    }));
  }, [
    qboInvoices,
    qboInvoicesLoading,
    viewInvoiceDialogState.invoice,
    viewInvoiceDialogState.isOpen,
  ]);

  useEffect(() => {
    if (!isImportingCustomers && !subscribeCustomerImportMounted.current) {
      return undefined;
    }
    subscribeCustomerImportMounted.current = true;
    const unsubscribe = DbRead.qbo.subscribeImportProgress({
      siteKey: props.siteKey,
      type: EnumQBOImportJobType.CUSTOMERS,
      onChange: setCustomersImportProgress,
      onError: logger.error,
    });
    return () => unsubscribe && unsubscribe();
  }, [props.siteKey, isImportingCustomers]);

  useEffect(() => {
    if (!isImportingItems && !subscribeItemsImportMounted.current) {
      return undefined;
    }
    subscribeItemsImportMounted.current = true;
    const unsubscribe = DbRead.qbo.subscribeImportProgress({
      siteKey: props.siteKey,
      type: EnumQBOImportJobType.PRICE_BOOK_ITEMS,
      onChange: setItemsImportProgress,
      onError: logger.error,
    });
    return () => unsubscribe && unsubscribe();
  }, [props.siteKey, isImportingItems]);

  useEffect(() => {
    if (
      !isImportingInvoicesAndPayments &&
      !subscribeInvoicesAndPaymentsImportMounted.current
    ) {
      return undefined;
    }
    subscribeInvoicesAndPaymentsImportMounted.current = true;
    const unsubscribe = DbRead.qbo.subscribeImportProgress({
      siteKey: props.siteKey,
      type: EnumQBOImportJobType.INVOICES_AND_PAYMENTS,
      onChange: setInvoicesAndPaymentsImportProgress,
      onError: logger.error,
    });
    return () => unsubscribe && unsubscribe();
  }, [props.siteKey, isImportingInvoicesAndPayments]);

  function onChangeInvoiceDateRange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ): void {
    try {
      setDateRange({
        start: toCalendarDate(value.start),
        end: toCalendarDate(value.end),
      });
    } catch (e) {
      logger.error(
        `(QB import) onChangeInvoiceDateRange: ${e instanceof Error ? e.message : JSON.stringify(e)}`,
      );
    }
  }

  /**
   * Handles the import of selected invoices and their payments. Handles user
   * feedback, won't throw an error.
   * @param selectedIDs - Stilt invoice IDs
   */
  async function handleImportSelectedInvoices(selectedIDs: string[]) {
    let failCount = 0;
    for (const id of selectedIDs) {
      const foundInv = invoices.find((i) => i.id === id);
      try {
        const res = await DbWrite.qbo.importInvoiceAndPayments(
          props.siteKey,
          id,
        );
        if (res.status !== 200 && res.status !== 201) {
          failCount++;
          addToastMessage({
            id: createToastMessageID(),
            message: `Failed to import invoice ${foundInv && foundInv.invoiceNumber ? foundInv.invoiceNumber : ""}: ${res.message}`,
            type: "error",
            dialog: false,
          });
        }
      } catch (e) {
        logger.error(
          `Unexpected error importing QBO invoice: ${e instanceof Error ? e.message : JSON.stringify(e)}`,
        );
        failCount++;
        addToastMessage({
          id: createToastMessageID(),
          message: `Failed to import invoice ${foundInv && foundInv.invoiceNumber ? foundInv.invoiceNumber : ""}: unknown error`,
          type: "error",
          dialog: false,
        });
      }
    }
    // feedback for user --
    if (selectedIDs.length === 1) {
      if (!failCount) {
        addToastMessage({
          id: createToastMessageID(),
          message: `Successfully imported invoice and any associated payments`,
          type: "success",
          dialog: false,
        });
      }
      return;
    }
    if (!failCount) {
      addToastMessage({
        id: createToastMessageID(),
        message: `Successfully imported ${selectedIDs.length} invoices and any associated payments`,
        type: "success",
        dialog: false,
      });
      return;
    }
    if (failCount !== selectedIDs.length) {
      addToastMessage({
        id: createToastMessageID(),
        message: `Successfully imported ${selectedIDs.length - failCount} of ${selectedIDs.length} invoices and any associated payments`,
        type: "success",
        dialog: false,
      });
    }
  }

  function handleViewInvoice(invoiceID: string, acctRefID: string | null) {
    setViewInvoiceDialogState({
      isOpen: true,
      invoice: invoices.find((i) => i.id === invoiceID) ?? null,
      qboInvoice: qboInvoices?.find((i) => i.id === acctRefID) ?? null,
      areQboInvoicesLoading: qboInvoicesLoading,
    });
  }

  const connectionButtons = useMemo(() => {
    if (siteKeyDocLoading || !siteKeyDoc) return null;
    return (
      siteKeyDoc &&
      userPermissions &&
      userPermissions.permissions.isSiteAdmin && (
        <ConnectionButtons
          siteKey={props.siteKey}
          qboRealmID={siteKeyDoc.customizations.accounting?.qboRealmID}
        />
      )
    );
  }, [siteKeyDoc, siteKeyDocLoading, userPermissions, props.siteKey]);

  const infoImportSingle = (
    <InfoDialogImportSingle
      isOpen={moreInfoBtnStates.importOptions.single}
      onClose={() =>
        setMoreInfoBtnStates((prev) => ({
          ...prev,
          importOptions: { ...prev.importOptions, single: false },
        }))
      }
    />
  );

  const infoImportAll = (
    <InfoDialogImportAll
      isOpen={moreInfoBtnStates.importOptions.all}
      onClose={() =>
        setMoreInfoBtnStates((prev) => ({
          ...prev,
          importOptions: { ...prev.importOptions, all: false },
        }))
      }
    />
  );

  const infoSingleByInvoice = (
    <InfoDialogImportSingleByInvoice
      isOpen={moreInfoBtnStates.singleOption.byInvoice}
      onClose={() =>
        setMoreInfoBtnStates((prev) => ({
          ...prev,
          singleOption: { ...prev.singleOption, byInvoice: false },
        }))
      }
    />
  );

  const infoSingleByCustomer = (
    <InfoDialogImportSingleByCustomer
      isOpen={moreInfoBtnStates.singleOption.byCustomer}
      onClose={() =>
        setMoreInfoBtnStates((prev) => ({
          ...prev,
          singleOption: { ...prev.singleOption, byCustomer: false },
        }))
      }
    />
  );

  /*
  const optionAllButtons = (
    <>
      <BaseButtonSecondary
        isSelected={selectedDataTypeAll === "all"}
        onClick={() => setSelectedDataTypeAll("all")}
      >
        All
      </BaseButtonSecondary>
      <BaseButtonSecondary
        isSelected={selectedDataTypeAll === "customers"}
        onClick={() => setSelectedDataTypeAll("customers")}
      >
        Customers
      </BaseButtonSecondary>
      <BaseButtonSecondary
        isSelected={selectedDataTypeAll === "pbItems"}
        onClick={() => setSelectedDataTypeAll("pbItems")}
      >
        PriceBook Items
      </BaseButtonSecondary>
      <BaseButtonSecondary
        isSelected={selectedDataTypeAll === "invoices"}
        onClick={() => setSelectedDataTypeAll("invoices")}
      >
        Invoices
      </BaseButtonSecondary>
      <BaseButtonSecondary
        isSelected={selectedDataTypeAll === "payments"}
        onClick={() => setSelectedDataTypeAll("payments")}
      >
        Payments
      </BaseButtonSecondary>
    </>
  );
  */

  /*
  const optionSingleButtons = (
    <>
      <div className="flex items-center gap-3">
        <BaseButtonSecondary
          isSelected={selectedDataTypeSingle === "byCustomer"}
          onClick={() => setSelectedDataTypeSingle("byCustomer")}
        >
          Import by Customer
        </BaseButtonSecondary>
        <ButtonColored
          kind="infoBlue"
          className={`${selectedDataTypeSingle === "byCustomer" ? "" : "invisible"}`}
          onClick={() => {
            setMoreInfoBtnStates((prev) => ({
              ...prev,
              singleOption: { ...prev.singleOption, byCustomer: true },
            }));
          }}
          overridePadding="p-2"
          overrideHeight=""
          aria-description="More Info: Importing by Customer"
        >
          <MdInfo className="h-5 w-5" />
        </ButtonColored>
      </div>
      <div className="flex items-center gap-3">
        <BaseButtonSecondary
          isSelected={selectedDataTypeSingle === "allInvoices"}
          onClick={() => setSelectedDataTypeSingle("allInvoices")}
        >
          Import by Invoice
        </BaseButtonSecondary>
        <ButtonColored
          kind="infoBlue"
          className={`${selectedDataTypeSingle === "allInvoices" ? "" : "invisible"}`}
          onClick={() => {
            setMoreInfoBtnStates((prev) => ({
              ...prev,
              singleOption: { ...prev.singleOption, byInvoice: true },
            }));
          }}
          overridePadding="p-2"
          overrideHeight=""
          aria-description="More Info: Importing by Invoice"
        >
          <MdInfo className="h-5 w-5" />
        </ButtonColored>
      </div>
    </>
  );
  */

  const customersImportProgressComponent = customersImportProgress && (
    <QBOImportProgressIndicator
      importJob={customersImportProgress}
      className="mt-4"
    />
  );

  const itemsImportProgressComponent = itemsImportProgress && (
    <QBOImportProgressIndicator
      importJob={itemsImportProgress}
      className="mt-4"
    />
  );

  const invoicesAndPaymentsImportProgressComponent =
    invoicesAndPaymentsImportProgress && (
      <QBOImportProgressIndicator
        importJob={invoicesAndPaymentsImportProgress}
        className="mt-4"
      />
    );

  // not using our traditional pattern of conditional rendering. want to reserve space
  // on the UI for the indicator, regardless of the state of the qbo invoices query.
  const qboLoadingInvoicesIndicator = (
    <StyledTooltip title="Fetching invoices from QuickBooks...">
      <div
        className={`${qboInvoicesLoading || qboInvoicesFetching ? "" : "invisible"}`}
      >
        <LoadingSpinner marginClass="" />
      </div>
    </StyledTooltip>
  );

  const dateRangePicker = (
    <DateRangePicker value={dateRange} onChange={onChangeInvoiceDateRange} />
  );

  const comparisonInvoice = siteKeyDoc && viewInvoiceDialogState.qboInvoice && (
    <QBOInvoice
      qboInvoice={viewInvoiceDialogState.qboInvoice}
      currency={siteKeyDoc.customizations.accounting?.currency ?? "USD"}
      stiltInvoiceNumber={viewInvoiceDialogState.invoice?.invoiceNumber ?? null}
      displayedOnInvoiceDialog
    />
  );

  const viewInvoiceDialog = siteKeyDoc && viewInvoiceDialogState.invoice && (
    <ViewSimplifiedInvoiceDialog
      isOpen={viewInvoiceDialogState.isOpen}
      onClose={() =>
        setViewInvoiceDialogState({
          isOpen: false,
          invoice: null,
          qboInvoice: null,
          areQboInvoicesLoading: qboInvoicesLoading,
        })
      }
      invoice={viewInvoiceDialogState.invoice}
      siteKeyID={props.siteKey}
      merchantName={siteKeyDoc.name}
      merchantLogoURL={
        siteKeyDoc.customizations.accounting?.merchantLogoURL ?? null
      }
      userIsSiteAdmin={userPermissions?.permissions.isSiteAdmin ?? false}
      userDisplayNamesMap={userDisplayNamesMap}
      children={{ comparisonInvoice: comparisonInvoice }}
    />
  );

  const importAllContent = (
    <ImportAllContent selectedDataTypeAll={selectedDataTypeAll} />
  );

  const importSingleAllInvoicesContent = (
    <ImportSingleAllInvoicesContent
      invoices={invoices}
      currency={siteKeyDoc?.customizations.accounting?.currency ?? "USD"}
      onViewInvoice={handleViewInvoice}
      onImportSelected={handleImportSelectedInvoices}
      qboInvoices={qboInvoices}
      qboInvoicesLoading={qboInvoicesLoading}
      siteKeyLocationList={siteKeyLocationList}
    >
      {{
        dateRangePicker: dateRangePicker,
        qboLoadingInvoicesIndicator: qboLoadingInvoicesIndicator,
      }}
    </ImportSingleAllInvoicesContent>
  );

  const importSingleByCustomerContent = (
    <ImportSingleByCustomerContent
      siteKey={props.siteKey}
      currency={siteKeyDoc?.customizations.accounting?.currency ?? "USD"}
      selectedCustomer={selectedCustomer}
      setSelectedCustomer={setSelectedCustomer}
      invoices={invoices}
      onViewInvoice={handleViewInvoice}
      onImportSelected={handleImportSelectedInvoices}
      qboInvoices={qboInvoices}
      qboInvoicesLoading={qboInvoicesLoading}
      siteKeyLocationList={siteKeyLocationList}
    >
      {{
        qboLoadingInvoicesIndicator: qboLoadingInvoicesIndicator,
      }}
    </ImportSingleByCustomerContent>
  );

  async function handleOpenImportQBOAccountsDialog() {
    if (!firebaseUser) {
      addToastMessage({
        id: createToastMessageID(),
        message: "User not authenticated. Please refresh.",
        type: "error",
        dialog: true,
      });
      return;
    }
    try {
      setIsLoadingQBOAccounts(true);
      const accounts = await DbRead.gLAccounts.getQBOAccounts(props.siteKey);
      setQboChartOfAccounts(accounts);
      setImportQBODialogOpen(true);
    } catch (error) {
      logger.error("Error fetching QuickBooks accounts:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Failed to fetch QuickBooks accounts. Please try again.",
        dialog: false,
        type: "error",
      });
    } finally {
      setIsLoadingQBOAccounts(false);
    }
  }

  async function handleImportSelectedGLAccounts(
    accountsToImportApi: GLAccount_CreateAPI[],
  ) {
    if (!firebaseUser) {
      addToastMessage({
        id: createToastMessageID(),
        message: "User not authenticated. Please refresh.",
        type: "error",
        dialog: true,
      });
      return;
    }
    try {
      const results = { success: 0, failed: 0 };
      for (const account of accountsToImportApi) {
        try {
          const newGLAccount: GLAccount_CreateAPI = {
            ...account,
            currency: siteKeyDoc?.customizations.accounting?.currency ?? "USD",
            deleted: false,
            lastModifiedBy: firebaseUser.uid,
            siteKey: props.siteKey,
          };
          const validatedAccount = GLAccountManager.parseCreate(newGLAccount);
          await DbWrite.gLAccounts.create(validatedAccount);
          results.success++;
        } catch (error) {
          logger.error(`Error importing account ${account.name}:`, error);
          results.failed++;
        }
      }

      if (results.success > 0) {
        addToastMessage({
          id: createToastMessageID(),
          message: `Successfully imported ${results.success} account${results.success !== 1 ? "s" : ""}${results.failed > 0 ? ` (${results.failed} failed)` : ""}`,
          dialog: false,
          type: "success",
        });
      } else if (results.failed > 0) {
        addToastMessage({
          id: createToastMessageID(),
          message: `Failed to import ${results.failed} account${results.failed !== 1 ? "s" : ""}`,
          dialog: false,
          type: "error",
        });
      }
      setImportQBODialogOpen(false);
    } catch (error) {
      logger.error("Error importing accounts:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "An error occurred while importing accounts",
        dialog: false,
        type: "error",
      });
    }
  }

  // --- Start: Logic for Customer Import Step ---
  async function handleImportAllCustomers() {
    if (!firebaseUser) {
      addToastMessage({
        id: createToastMessageID(),
        message: "User not authenticated. Please refresh.",
        type: "error",
        dialog: true,
      });
      return;
    }
    setIsImportingCustomers(true);
    try {
      const result = await DbWrite.qbo.importAllCustomers(props.siteKey);

      if (
        result &&
        (result.status === 200 ||
          result.status === 201 ||
          result.status === 202)
      ) {
        addToastMessage({
          id: createToastMessageID(),
          message:
            result.message ||
            "Customer import process initiated successfully. This may take a few minutes.",
          type: "success",
          dialog: false,
        });
      } else {
        addToastMessage({
          id: createToastMessageID(),
          message: result.message || "Failed to start customer import process.",
          type: "error",
          dialog: false,
        });
      }
    } catch (error) {
      logger.error("Error calling getAllQuickbooksCustomers:", error);
      addToastMessage({
        id: createToastMessageID(),
        message:
          error instanceof Error
            ? error.message
            : "Failed to start customer import process.",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsImportingCustomers(false);
    }
  }
  // --- End: Logic for Customer Import Step ---

  async function handleImportAllItems() {
    if (!firebaseUser) throw new Error("User not authenticated");
    setIsImportingItems(true);
    try {
      const result = await DbWrite.qbo.importAllItems(props.siteKey);

      if (result && result.status === 200) {
        addToastMessage({
          id: createToastMessageID(),
          message:
            result.message ||
            "Price book item import process initiated successfully. This may take a few minutes.",
          type: "success",
          dialog: false,
        });
      } else {
        addToastMessage({
          id: createToastMessageID(),
          message:
            result.message || "Failed to start price book item import process.",
          type: "error",
          dialog: false,
        });
      }
    } catch (error) {
      logger.error("Error calling importAllItems", error);
      addToastMessage({
        id: createToastMessageID(),
        message:
          error instanceof Error
            ? error.message
            : "Failed to start price book item import process.",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsImportingItems(false);
    }
  }

  async function handleImportAllInvoicesAndPayments() {
    if (!firebaseUser) throw new Error("User not authenticated");
    setIsImportingInvoicesAndPayments(true);
    try {
      const result = await DbWrite.qbo.importAllInvoicesAndPayments(
        props.siteKey,
      );

      if (result && result.status === 200) {
        addToastMessage({
          id: createToastMessageID(),
          message:
            result.message ||
            "Invoice and payment import process initiated successfully. This may take a few minutes.",
          type: "success",
          dialog: false,
        });
      } else {
        addToastMessage({
          id: createToastMessageID(),
          message:
            result.message ||
            "Failed to start invoice and payment import process.",
          type: "error",
          dialog: false,
        });
      }
    } catch (error) {
      logger.error("Error calling importAllInvoicesAndPayments", error);
      addToastMessage({
        id: createToastMessageID(),
        message:
          error instanceof Error
            ? error.message
            : "Failed to start invoice and payment import process.",
        type: "error",
        dialog: false,
      });
    } finally {
      setIsImportingInvoicesAndPayments(false);
    }
  }

  const importQBOAccountsDialog = siteKeyDoc && firebaseUser && (
    <ImportQBOAccountsDialog
      isDialogOpen={importQBODialogOpen}
      closeDialog={() => setImportQBODialogOpen(false)}
      siteKey={props.siteKey}
      firebaseUser={firebaseUser}
      currency={siteKeyDoc.customizations.accounting?.currency ?? "USD"}
      onImportAccounts={handleImportSelectedGLAccounts}
      qboAccounts={qboChartOfAccounts}
      existingGLAccounts={gLAccountList}
      isLoading={isLoadingQBOAccounts}
    />
  );

  const handleNextStep = () => {
    if (currentStepIndex < IMPORT_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      addToastMessage({
        id: createToastMessageID(),
        message: "QuickBooks Import Setup Complete!",
        type: "success",
        dialog: false,
      });
    }
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Function to fetch all record counts
  const fetchAllCounts = useCallback(async () => {
    if (!isQBConnected || !siteKeyDoc || !props.siteKey) {
      // Clear counts if conditions are not met, or set loading to false if already true
      setQboRecordCounts(null);
      setStiltRecordCounts(null);
      setIsLoadingRecordCounts(false);
      setIsLoadingStiltRecordCounts(false);
      return;
    }

    // Fetch QBO Counts
    setIsLoadingRecordCounts(true);
    try {
      const qboCounts = await DbRead.qbo.getRecordCounts(props.siteKey);
      setQboRecordCounts(qboCounts as Record<string, number>);
    } catch (error) {
      logger.error("Error fetching QBO record counts:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Could not load record counts from QuickBooks.",
        type: "warning",
        dialog: false,
      });
      setQboRecordCounts(null);
    } finally {
      setIsLoadingRecordCounts(false);
    }

    // Fetch Stilt Counts
    setIsLoadingStiltRecordCounts(true);
    try {
      const stiltCountsResult = await DbRead.aggregates.getRecordCounts(
        props.siteKey,
      );
      setStiltRecordCounts(stiltCountsResult as Record<string, number>);
    } catch (error) {
      logger.error("Error fetching Stilt record counts:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Could not load existing record counts from Stilt.",
        type: "warning",
        dialog: false,
      });
      setStiltRecordCounts(null);
    } finally {
      setIsLoadingStiltRecordCounts(false);
    }
  }, [isQBConnected, siteKeyDoc, props.siteKey, addToastMessage]); // Add all dependencies used inside

  // Fetch QBO and Stilt Record Counts when connection status or siteKeyDoc changes
  useEffect(() => {
    fetchAllCounts();
  }, [fetchAllCounts]); // fetchAllCounts is memoized with useCallback, so this effect runs when it changes (i.e., its deps change)

  if (siteKeyDocLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  return (
    <>
      <QuickbooksImportPage
        siteKey={props.siteKey}
        isConnected={isQBConnected}
        currentStep={currentImportStep}
        currentStepIndex={currentStepIndex}
        totalSteps={IMPORT_STEPS.length}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        onOpenImportAccountsDialog={handleOpenImportQBOAccountsDialog}
        isLoadingQBOAccounts={isLoadingQBOAccounts}
        gLAccountList={gLAccountList}
        onImportAllCustomers={handleImportAllCustomers}
        isImportingCustomers={isImportingCustomers}
        onImportAllItems={handleImportAllItems}
        isImportingItems={isImportingItems}
        onImportAllInvoicesAndPayments={handleImportAllInvoicesAndPayments}
        isImportingInvoicesAndPayments={isImportingInvoicesAndPayments}
        qboRecordCounts={qboRecordCounts}
        isLoadingRecordCounts={isLoadingRecordCounts}
        stiltRecordCounts={stiltRecordCounts}
        isLoadingStiltRecordCounts={isLoadingStiltRecordCounts}
      >
        {{
          connectionSection: connectionButtons,
          customersImportProgress: customersImportProgressComponent,
          itemsImportProgress: itemsImportProgressComponent,
          invoicesAndPaymentsImportProgress:
            invoicesAndPaymentsImportProgressComponent,

          importAllContent: importAllContent,
          importSingleByCustomerContent: importSingleByCustomerContent,
          importSingleAllInvoicesContent: importSingleAllInvoicesContent,
        }}
      </QuickbooksImportPage>
      {viewInvoiceDialog}
      {importQBOAccountsDialog}
      {infoImportSingle}
      {infoImportAll}
      {infoSingleByInvoice}
      {infoSingleByCustomer}
    </>
  );
}

// SECTION: HELPERS

// #region More Info Dialogs
function InfoDialogBase(props: {
  isOpen: boolean;
  onClose: () => void;
  headingText: string;
  children: React.ReactNode;
}): JSX.Element {
  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl font-semibold">
        {props.headingText}
      </h1>
      <button type="button" onClick={props.onClose}>
        <XMarkIcon aria-label={`close dialog`} className="h-6 text-white" />
      </button>
    </div>
  );
  return (
    <BaseModal
      open={props.isOpen}
      closeModal={props.onClose}
      allowOverflowY={true}
      title={header}
      parentDivStyles="text-left max-w-4xl"
    >
      <div className="pb-8 pl-6 pr-8 pt-2">{props.children}</div>
    </BaseModal>
  );
}

function InfoDialogImportSingle(props: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <InfoDialogBase headingText="Import Select Records" {...props}>
      <ul className="list-disc space-y-2 pl-8">
        <li>
          <span className="font-semibold">By Customer: </span>
          View all invoices for a specific customer, selectively import the
          customer's invoices and payments.
        </li>
        <li>
          <span className="font-semibold">By Invoice: </span>
          View all invoices issued within the specified date range, selectively
          import invoices and payments.
        </li>
      </ul>
    </InfoDialogBase>
  );
}

function InfoDialogImportAll(props: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <InfoDialogBase headingText="Import Entire Sets" {...props}>
      <div className="flex items-start gap-2">
        <WarningIcon className="h-5 w-5 text-orange-600" />
        <p>
          You likely only want to import entire datasets if your site is brand
          new. If you've been using Stilt for a while, exercise caution when
          using this option.
        </p>
      </div>
    </InfoDialogBase>
  );
}

function InfoDialogImportSingleByInvoice(props: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <InfoDialogBase headingText="Importing by Invoice" {...props}>
      <ul className="list-disc space-y-2 pl-8">
        <li>
          Select a date range to view all Stilt invoices that were issued within
          that range. Invoices that have not been pushed to QuickBooks will not
          be shown.
        </li>
        <li>
          You can import invoices from QuickBooks selectively, or en masse. When
          you import an invoice, you will also be importing any payments that
          QuickBooks associates with the invoice. This will overwrite the Stilt
          data with the data retrieved from QuickBooks.
        </li>
        <li>
          View an invoice by clicking the row. If the QuickBooks amounts do not
          match the Stilt amounts, the row will be highlighted. False positives
          will occur if any refunds were posted against the Stilt invoice.
        </li>
      </ul>
    </InfoDialogBase>
  );
}

function InfoDialogImportSingleByCustomer(props: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <InfoDialogBase headingText="Importing by Customer" {...props}>
      <ul className="list-disc space-y-2 pl-8">
        <li>
          Select a customer to view all of their Stilt invoices. You can then
          import invoices from QuickBooks selectively, or en masse. Customers
          available to select from are limited to those that have synced with
          QuickBooks.
        </li>
        <li>
          When you import an invoice, you will also be importing any payments
          that QuickBooks associates with the invoice. This will overwrite the
          Stilt data with the data retrieved from QuickBooks.
        </li>
      </ul>
    </InfoDialogBase>
  );
}
// #endregion
